import React, { Component } from "react";

class SignatureField extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isDrawing: false,
      hasSignature: false,
      isUploading: false,
      uploaded: false,
      uploadedFileInfo: null,
      isDeleting: false,
    };
    this.canvasRef = React.createRef();
    this.lastPoint = null;
  }

  componentDidMount() {
    this.setupCanvas();
    window.addEventListener("resize", this.setupCanvas);
  }

  componentDidUpdate(prevProps) {
    // Sync with form state when it changes externally
    // This is important after signature upload when the value changes from File object to file info object
    if (this.props.defaultValue !== prevProps.defaultValue && this.props.defaultValue) {
      this.setState({ uploadedFileInfo: this.props.defaultValue, uploaded: true, hasSignature: false });
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.setupCanvas);
  }

  setupCanvas = () => {
    const canvas = this.canvasRef.current;
    if (!canvas) return;

    const container = canvas.parentElement;
    const rect = container.getBoundingClientRect();

    // Set display size
    canvas.style.width = "100%";
    canvas.style.height = "200px";

    // Set actual canvas resolution
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = 200 * dpr;

    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2;
    ctx.strokeStyle = "#000";

    // Fill with white background
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  getPosition = (e) => {
    const canvas = this.canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    if (e.touches && e.touches.length > 0) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    }
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  startDrawing = (e) => {
    e.preventDefault();
    const pos = this.getPosition(e);
    this.lastPoint = pos;
    this.setState({ isDrawing: true, uploaded: false });

    const ctx = this.canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  draw = (e) => {
    if (!this.state.isDrawing) return;
    e.preventDefault();

    const pos = this.getPosition(e);
    const ctx = this.canvasRef.current.getContext("2d");

    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    this.lastPoint = pos;
  };

  stopDrawing = (e) => {
    if (!this.state.isDrawing) return;
    e.preventDefault();
    this.setState({ isDrawing: false, hasSignature: true });
    this.lastPoint = null;
  };

  clearCanvas = () => {
    const canvas = this.canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    this.setState({ hasSignature: false, uploaded: false, uploadedFileInfo: null });

    this.props.setValue(this.props.name, null, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  handleDeleteSignature = async () => {
    const { uploadedFileInfo } = this.state;
    const { deleteFile, name, setValue } = this.props;

    // Extract file ID from uploadedFileInfo
    let fileId = null;
    if (uploadedFileInfo) {
      if (typeof uploadedFileInfo === 'number') {
        fileId = uploadedFileInfo;
      } else if (typeof uploadedFileInfo === 'string') {
        fileId = uploadedFileInfo;
      } else if (typeof uploadedFileInfo === 'object' && uploadedFileInfo.id) {
        fileId = uploadedFileInfo.id;
      }
    }

    // If we have an ID and deleteFile function, delete it from the server
    if (deleteFile && fileId) {
      this.setState({ isDeleting: true });
      try {
        await deleteFile(fileId, name);
      } catch (error) {
        console.error('[SignatureField] Error deleting signature:', error);
        this.setState({ isDeleting: false });
        return;
      }
    }

    this.setState({
      uploadedFileInfo: null,
      uploaded: false,
      hasSignature: false,
      isDeleting: false
    });
    setValue(name, null, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true
    });
  };

  saveSignature = async () => {
    if (!this.state.hasSignature) return;

    const canvas = this.canvasRef.current;

    try {
      this.setState({ isUploading: true });

      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/png")
      );

      const fileName = `signature_${Date.now()}.png`;
      const file = new File([blob], fileName, {
        type: "image/png",
      });

      if (this.props.uploadFile) {
        const uploadResult = await this.props.uploadFile(file, this.props.name);
        // The uploadFile function now returns the file info with id and name
        // Store it for later deletion
        this.setState({ uploaded: true, uploadedFileInfo: uploadResult });

        // Update the form state with the uploaded file info
        this.props.setValue(this.props.name, [uploadResult.id], {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });
      }
    } catch (error) {
      console.error("Signature save error:", error);
    } finally {
      this.setState({ isUploading: false });
    }
  };

  render() {
    const {
      register,
      errors,
      label,
      name,
      rules,
      className,
      labelClassName,
      labelStyles,
      uploadError,
    } = this.props;

    const { hasSignature, isUploading, uploaded, uploadedFileInfo, isDeleting } = this.state;

    return (
      <div className={`flex flex-col items-start ${className || ""}`}>
        <label
          className={`form-label font-bold float-start mb-1 ${labelClassName || ""}`}
          style={labelStyles}
        >
          {rules?.required && <span className="text-red-500">*</span>} {label}
        </label>

        <div className="w-full">
          <div
            className={`relative border-2 rounded-lg overflow-hidden transition-all duration-200 ${
              uploaded
                ? "border-green-400 bg-green-50"
                : "border-gray-300"
            }`}
          >
            {isUploading && (
              <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                <div className="flex flex-col items-center space-y-2">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  <span className="text-sm text-gray-600">Saving...</span>
                </div>
              </div>
            )}

            <canvas
              ref={this.canvasRef}
              className="cursor-crosshair w-full touch-none"
              onMouseDown={this.startDrawing}
              onMouseMove={this.draw}
              onMouseUp={this.stopDrawing}
              onMouseLeave={this.stopDrawing}
              onTouchStart={this.startDrawing}
              onTouchMove={this.draw}
              onTouchEnd={this.stopDrawing}
            />
          </div>

          <div className="flex items-center gap-2 mt-2">
            <button
              type="button"
              onClick={this.clearCanvas}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-600 hover:bg-gray-100 transition-colors"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={this.saveSignature}
              disabled={!hasSignature || isUploading || uploaded}
              className={`px-3 py-1.5 text-sm rounded-md text-white transition-colors ${
                !hasSignature || isUploading || uploaded
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-blue-500 hover:bg-blue-600"
              }`}
            >
              {uploaded ? "Saved" : "Save Signature"}
            </button>
            {uploaded && uploadedFileInfo && (
              <button
                type="button"
                onClick={this.handleDeleteSignature}
                disabled={isDeleting || isUploading}
                className="text-red-500 hover:text-red-700 p-2 transition-colors disabled:opacity-50"
                title="Delete signature"
              >
                {isDeleting ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-500"></div>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                )}
              </button>
            )}
            {uploaded && uploadedFileInfo && (
              <span className="text-sm text-green-600 flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                {typeof uploadedFileInfo === 'object' && uploadedFileInfo.name
                  ? uploadedFileInfo.name
                  : "Uploaded"}
              </span>
            )}
          </div>

          <input type="hidden" {...register(name, rules)} />
        </div>

        {(errors[name] || uploadError) && (
          <span className="text-red-500 text-sm mt-1">
            {errors[name]?.message || uploadError}
          </span>
        )}
      </div>
    );
  }
}

export default SignatureField;
